import React, { useMemo } from 'react';
import { PersonTypes } from '@frontend/api-person';
import { Trans, useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { BannerNotification, Heading } from '@frontend/design-system';
import { useGetHasInsuranceVerificationAccess } from './hooks';
import { InsuranceDetails } from './insurance-details';

export interface InsuranceDetailsContainerProps {
  locationId: string;
  person?: PersonTypes.Person;
}

export const InsuranceDetailsContainer = React.memo(({ locationId, person }: InsuranceDetailsContainerProps) => {
  const {
    hasWeaveInsuranceVerificationFullAccess,
    isMultiLocation,
    allChildLocationIdDoNotHaveAccess,
    locationIdsWithAccess,
  } = useGetHasInsuranceVerificationAccess(locationId);

  const insuranceDetailsProps = useMemo(() => ({ locationId, person }), [locationId, person]);

  const hasNoAccess = isMultiLocation ? allChildLocationIdDoNotHaveAccess : !hasWeaveInsuranceVerificationFullAccess;

  // If the user has no access to insurance verification, we do not show the insurance section in the person details section
  if (hasNoAccess) {
    return null;
  }

  // If the location is multi-location and the user does not have access to insurance verification for the current location of the person/patient,
  // then we showcase a message indicating that the user does not have access to insurance verification for this location
  if (isMultiLocation && !hasWeaveInsuranceVerificationFullAccess) {
    return <NoInsuranceVerificationAccess locationId={locationId} locationIdsWithAccess={locationIdsWithAccess} />;
  }

  // If the user has access to insurance verification, we show the insurance details
  return <InsuranceDetails {...insuranceDetailsProps} />;
});

InsuranceDetailsContainer.displayName = 'InsuranceDetailsContainer';

type NoInsuranceVerificationAccessProps = {
  locationId: string;
  locationIdsWithAccess: string[];
};

const NoInsuranceVerificationAccess = React.memo(
  ({ locationId, locationIdsWithAccess }: NoInsuranceVerificationAccessProps) => {
    const { t } = useTranslation('insuranceDetails');
    const { accessibleLocationData } = useAppScopeStore();
    const locationData = accessibleLocationData[locationId];
    const locationName = locationData?.name || '';

    const locationNames = locationIdsWithAccess
      ?.map((id) => accessibleLocationData[id]?.name)
      .filter(Boolean)
      .join(', ');

    const messageWithoutLocationName = t('You do not have permission to insurance verification for this location');

    return (
      <section css={{ padding: theme.spacing(2, 2, 1) }}>
        <header css={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing(2) }}>
          <Heading level={3}>{t('Insurance')}</Heading>
        </header>
        <div css={{ margin: theme.spacing(2, 0) }}>
          <BannerNotification
            status='warn'
            message={
              locationName ? (
                <Trans t={t}>
                  You don't have permission to view Insurance Verification for the location <b>{{ locationName }}</b>.
                  Please switch to one of the following accessible locations: <b>{{ locationNames }}</b>
                </Trans>
              ) : (
                messageWithoutLocationName
              )
            }
          />
        </div>
      </section>
    );
  }
);

NoInsuranceVerificationAccess.displayName = 'NoInsuranceVerificationAccess';
